import type { CSSProperties, ReactNode } from 'react';
import type { OrdTypeEnum } from '../../types/types';
import { Flex } from '../Core';
import { HStack } from '../Core/HStack';
import { Table, TableSize, Tbody, Td, Th, Tr, type TableProps } from '../Table';
import { Text } from '../Text';
import { TextDivider } from '../TextDivider';
import type { ParameterRow } from './useParameterRows';
import { DEFAULT_STRATEGY_PARAM_SECTION, useParameterRowsGroupedBySection } from './useParameterRows';

export {
  DEFAULT_STRATEGY_PARAM_SECTION,
  useParameterRows,
  useParameterRowsGroupedBySection,
  useStrategyParameterSections,
} from './useParameterRows';
export type { ParameterRow, ParameterRowsGroupedBySection } from './useParameterRows';
export interface WithStrategy {
  Strategy?: string;
  OrdType?: OrdTypeEnum;
  Parameters?: { [key: string]: any };
  EndTime?: string;
}

interface ParameterRowDecoration {
  label?: ReactNode;
  value?: ReactNode;
  labelSuffix?: ReactNode;
  labelStyle?: CSSProperties;
  rowStyle?: CSSProperties;
}

/**
 * `ParametersTable` supports two data modes:
 * 1) `rows` (preferred): caller provides already-resolved display rows.
 * 2) `order` (legacy convenience): rows are generated internally from strategy + parameters.
 *
 * When both are provided, `rows` wins and `order` is ignored.
 */
export interface ParametersTableProps extends Omit<TableProps, 'border'> {
  order?: WithStrategy;
  rows?: ParameterRow[];
  showEmptyParams?: boolean;
  decorateRow?: (row: ParameterRow) => ParameterRowDecoration | undefined;
}

export function ParametersTable({ order, rows, showEmptyParams = false, decorateRow, ...props }: ParametersTableProps) {
  if (rows !== undefined) {
    return <ParametersTableRows rows={rows} decorateRow={decorateRow} {...props} />;
  }

  return <OrderParametersTable order={order} showEmptyParams={showEmptyParams} decorateRow={decorateRow} {...props} />;
}

interface ParametersTableRowsProps extends Omit<TableProps, 'border'> {
  rows: ParameterRow[];
  decorateRow?: (row: ParameterRow) => ParameterRowDecoration | undefined;
  testIdPrefix?: string;
}

export interface OrderParametersTableProps extends Omit<TableProps, 'border'> {
  order?: WithStrategy;
  showEmptyParams?: boolean;
  decorateRow?: (row: ParameterRow) => ParameterRowDecoration | undefined;
}

export function OrderParametersTable({
  order,
  showEmptyParams = false,
  decorateRow,
  ...props
}: OrderParametersTableProps) {
  const { sectionRows, sectionNames } = useParameterRowsGroupedBySection(
    order?.Strategy,
    order?.Parameters,
    showEmptyParams,
    [],
    order?.EndTime
  );

  const hasAnyRows = sectionNames.some(section => (sectionRows[section]?.length ?? 0) > 0);

  if (!hasAnyRows) {
    return <Text color="colorTextSubtle">No parameters</Text>;
  }

  return (
    <Flex flexDirection="column" gap={0}>
      {sectionNames.map(sectionName => {
        const rows = sectionRows[sectionName];
        if (!rows || rows.length === 0) {
          return null;
        }
        const isDefaultSection = sectionName === DEFAULT_STRATEGY_PARAM_SECTION;
        const testIdPrefix = isDefaultSection ? 'order-param' : `order-param-${sectionName}`;
        return (
          <Flex key={sectionName} flexDirection="column" data-testid={`order-params-section-${sectionName}`}>
            {!isDefaultSection && (
              <TextDivider
                text={sectionName}
                textProps={{ fontSize: 'fontSizeSmall', whiteSpace: 'nowrap', weight: 'fontWeightLight' }}
                mt="spacing12"
                mb="spacing4"
              />
            )}
            <ParametersTableRows rows={rows} decorateRow={decorateRow} testIdPrefix={testIdPrefix} {...props} />
          </Flex>
        );
      })}
    </Flex>
  );
}

function ParametersTableRows({ rows, decorateRow, testIdPrefix = 'order-param', ...props }: ParametersTableRowsProps) {
  if (rows.length === 0) {
    return <Text color="colorTextSubtle">No parameters</Text>;
  }

  return (
    <Table size={TableSize.Small} {...props}>
      <Tbody>
        {rows.map((row, index) => {
          const rowDecoration = decorateRow?.(row);
          const labelSuffix = rowDecoration?.labelSuffix;
          const labelContent = rowDecoration?.label ?? row.title;
          const rowKey = row.name ?? `${row.title}-${index}`;

          return (
            <Tr key={rowKey} data-param-name={row.name} style={rowDecoration?.rowStyle}>
              <Th data-param-name={row.name} style={rowDecoration?.labelStyle}>
                {rowDecoration?.label ? (
                  labelContent
                ) : labelSuffix ? (
                  <HStack as="span" display="inline-flex" alignItems="center" gap="spacing4">
                    <Text size="fontSizeSmall" color="colorTextDefault">
                      {labelContent}
                    </Text>
                    {labelSuffix}
                  </HStack>
                ) : (
                  labelContent
                )}
              </Th>
              <Td align="right" data-testid={`${testIdPrefix}-${rowKey}`} data-param-name={row.name}>
                {rowDecoration?.value ?? row.value}
              </Td>
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );
}
